/**
 * `/internal/support-tickets` wire contract (#1394, ops half).
 *
 * snake_case end to end: ops-api relays these bodies verbatim (its proxy
 * preserves response bytes) and ops-console reads the snake_case fields
 * directly — there is deliberately no camel conversion on the operator
 * surface. The tenant-facing camelCase shapes live in `public-tickets.ts`;
 * the two surfaces share the underlying rows but not their wire types.
 *
 * Unlike the tenant surface, vendor reply content is UNBOUNDED (#1394,
 * explicit): operator answers carry error output, request ids and
 * step-by-step instructions and routinely exceed the tenant-side 500.
 */

import type { TicketReplyAuthor, TicketStatus, TicketType } from './public-tickets.ts';

export interface OpsTicketSummary {
  readonly id: string;
  readonly org_id: string;
  readonly account_id: string;
  readonly type: TicketType;
  readonly email: string;
  readonly description: string;
  readonly status: TicketStatus;
  readonly attachment_count: number;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface OpsTicketReply {
  readonly id: string;
  readonly ticket_id: string;
  readonly author: TicketReplyAuthor;
  /** Set when author='user'. */
  readonly account_id: string | null;
  /** Set when author='vendor' — in-row operator attribution (#1397 delta). */
  readonly ops_user_id: string | null;
  readonly content: string;
  readonly created_at: string;
}

export interface OpsTicketAttachment {
  readonly id: string;
  readonly ticket_id: string;
  readonly file_name: string;
  readonly content_type: string;
  readonly size_bytes: number;
  readonly created_at: string;
}

/**
 * Sortable columns on the ops ticket queue (#1946).
 *
 * Every column the console renders except the row-expand control, so an
 * operator never meets a header that looks clickable and is not. The list is
 * server-paginated: sorting has to travel with the request, because ordering
 * the fifty rows already in the browser answers a question about the page
 * rather than about the queue.
 */
export const OPS_TICKET_SORT_KEYS = [
  'id',
  'org_id',
  'type',
  'status',
  'email',
  'attachment_count',
  'created_at',
  'updated_at',
] as const;

export type OpsTicketSortKey = (typeof OPS_TICKET_SORT_KEYS)[number];

export type OpsTicketSortDir = 'asc' | 'desc';

/**
 * Newest ticket first. Deliberately NOT the historical `updated_at ASC`
 * ("what has waited longest"): that view is still one click away on the
 * 更新时间 header, but it opened the queue on rows whose only claim to the top
 * was that nobody had touched them.
 */
export const OPS_TICKET_SORT_DEFAULT_KEY: OpsTicketSortKey = 'created_at';
export const OPS_TICKET_SORT_DEFAULT_DIR: OpsTicketSortDir = 'desc';

export interface OpsListTicketsResponse {
  readonly tickets: readonly OpsTicketSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface OpsTicketDetailResponse {
  readonly ticket: OpsTicketSummary;
  readonly replies: readonly OpsTicketReply[];
  readonly attachments: readonly OpsTicketAttachment[];
}

export interface OpsUpdateTicketStatusRequest {
  readonly status: TicketStatus;
}

export interface OpsUpdateTicketStatusResponse {
  readonly ticket: OpsTicketSummary;
}

export interface OpsCreateTicketReplyRequest {
  readonly content: string;
}

export interface OpsCreateTicketReplyResponse {
  readonly reply: OpsTicketReply;
}

export interface OpsTicketAttachmentDownloadResponse {
  readonly attachment: OpsTicketAttachment;
  readonly data_base64: string;
}
